import { GanttMarkerLine, GanttProvider } from '@/components/gantt/gantt/GanttProvider';
import { GanttTimerBar } from '@/components/gantt/gantt/GanttTimerBar';
import { Card } from '@/components/shared/card/Card';
import { EMPLOYEE_NAME_COLUMN_WIDTH } from '@/helpers/constants';
import { hoursToSeconds, secondsToHours } from '@/helpers/dateHelper';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { OpenClosingTimeOptions } from '@/services/settings/systemSettings.types';
import { useOpenClosingTimePref } from '@/services/settings/useOpenClosingTimePref';
import { WorkingRoster } from '@/types/roster.types';
import { FC, useCallback, useMemo } from 'react';
import { FiPlus, FiUsers } from 'react-icons/fi';
import uuid from 'react-uuid';
import { useSubsections } from '../roster/hooks/useSubsections';
import { shiftsWrapperId } from '../roster/staffPicker/rosterPickerHelper';
import { TemplateTaskDrawer } from './TemplateTaskDrawer';
import { TemplateShifts } from './TemplateShifts';

interface Props {
  workingTemplate?: WorkingRoster;
  setWorkingTemplate: (template: WorkingRoster) => void;
}

export const TemplateEditor: FC<Props> = ({ workingTemplate, setWorkingTemplate }) => {
  const minuteIncrement = useSystemSettings(state => state.settings.minuteIncrement);
  const minShiftDuration = useSystemSettings(state => state.settings.minShiftDuration);
  const subsections = useSubsections();
  const openClosingTimesPref = useOpenClosingTimePref();

  const markerLines: GanttMarkerLine[] = useMemo(() => {
    if (!workingTemplate || openClosingTimesPref !== OpenClosingTimeOptions.all) {
      return [];
    }

    const lines: GanttMarkerLine[] = [];
    if (workingTemplate.openTime) {
      lines.push({ time: workingTemplate.openTime, color: '#22c55e' });
    }
    if (workingTemplate.closeTime) {
      lines.push({ time: workingTemplate.closeTime, color: '#ef4444' });
    }

    return lines;
  }, [openClosingTimesPref, workingTemplate]);

  const addShift = useCallback(() => {
    if (!workingTemplate) {
      return;
    }

    setWorkingTemplate({
      ...workingTemplate,
      shifts: [
        ...workingTemplate.shifts,
        {
          id: `CREATE-${uuid()}`,
          start: hoursToSeconds(9),
          end: hoursToSeconds(17),
          breaks: [],
          tasks: []
        }
      ]
    });
  }, [setWorkingTemplate, workingTemplate]);

  if (!workingTemplate) {
    return <Card className="m-6 p-6">No template selected.</Card>;
  }

  const hoursCovered = secondsToHours(workingTemplate.endTime - workingTemplate.startTime);

  return (
    <GanttProvider
      markerLines={markerLines}
      subsections={subsections}
      minTime={workingTemplate.startTime}
      maxTime={workingTemplate.endTime}
      minMainLength={minShiftDuration}
      minuteIncrement={minuteIncrement}
    >
      <div className="flex-1 flex flex-col overflow-hidden" id={shiftsWrapperId}>
        <div className="overflow-scroll relative bg-white">
          <div style={{ minWidth: 100 * hoursCovered }}>
            <div className="flex sticky top-0 z-[100] bg-white">
              <div
                className="border-b border-r px-3 sticky left-0 z-[60] flex items-center text-green-600 font-bold gap-3 bg-white"
                style={{ width: EMPLOYEE_NAME_COLUMN_WIDTH }}
              >
                <div className="border-b-[3px] w-full border-green-600 flex items-center gap-2 py-1">
                  <FiUsers className="h-4 w-4" />
                  <span>Template shifts</span>
                </div>
                <button
                  className="bg-green-600 h-6 w-6 rounded-full flex items-center justify-center"
                  onClick={addShift}
                >
                  <FiPlus className="h-6 w-6 text-white" />
                </button>
              </div>
              <div className="flex-1 bg-white">
                <GanttTimerBar />
              </div>
            </div>
            <TemplateShifts workingTemplate={workingTemplate} setWorkingTemplate={setWorkingTemplate} />
          </div>
        </div>
        <div className="flex-1"></div>
        <TemplateTaskDrawer />
      </div>
    </GanttProvider>
  );
};
